/**
 * Hand-authored local context for province-level "near me" pages (TH + EN).
 * Facts are limited to geography, which public offices sit in or near the
 * province, and the kinds of cases typical there. No prices, no processing
 * times, no claims that a regional office offers a given desk unless the
 * Department of Consular Affairs states it — readers are told to confirm.
 */
export type ProvinceNote = { th: string[]; en: string[] };

const CONFIRM_TH = "ก่อนเดินทางควรโทรยืนยันกับสำนักงานว่าเปิดรับงานประเภทนั้นในวันที่จะไป";
const CONFIRM_EN = "Call the office before travelling to confirm it handles that request on the day.";

export const PROVINCE_NOTES: Record<string, ProvinceNote> = {
  nonthaburi: {
    th: [
      "นนทบุรีติดกับเขตหลักสี่ ที่ตั้งกรมการกงสุล ถนนแจ้งวัฒนะ ซึ่งรับงานรับรองนิติกรณ์เอกสารส่วนกลาง จึงยื่นด้วยตนเองได้สะดวกเมื่อจองคิวล่วงหน้า",
      "ศูนย์ราชการแจ้งวัฒนะอยู่ใกล้กัน เหมาะกับเคสที่ต้องติดต่อหลายหน่วยงานในวันเดียว",
    ],
    en: [
      "Nonthaburi borders Lak Si district, where the Department of Consular Affairs on Chaeng Watthana Road runs the central legalization desk; walk-in is practical with an online queue booking.",
      "The Chaeng Watthana Government Complex is nearby, useful when one case needs several agencies on the same day.",
    ],
  },
  pathumthani: {
    th: [
      "ปทุมธานีเดินทางถึงกรมการกงสุล แจ้งวัฒนะ ได้ในระยะใกล้ จึงมักวางแผนยื่นรับรองนิติกรณ์ที่ส่วนกลาง",
      "ผู้ใช้บริการในพื้นที่มีทั้งนักศึกษามหาวิทยาลัยในเขตรังสิต-คลองหลวง และพนักงานนิคมอุตสาหกรรม ซึ่งมักต้องเตรียมวุฒิการศึกษาหรือเอกสารใบอนุญาตทำงาน",
    ],
    en: [
      "Pathum Thani is a short trip to the Department of Consular Affairs on Chaeng Watthana, so legalization is usually filed centrally.",
      "Typical clients include university students around Rangsit–Khlong Luang and industrial-estate staff, who often need degree certificates or work-permit documents prepared.",
    ],
  },
  samutprakan: {
    th: [
      "สมุทรปราการมีนิคมอุตสาหกรรมหลายแห่งและอยู่ใกล้สนามบินสุวรรณภูมิ เคสที่พบบ่อยคือเอกสารบริษัทและเอกสารพนักงานต่างชาติ",
      "งานรับรองนิติกรณ์ส่วนใหญ่ยื่นที่กรมการกงสุล แจ้งวัฒนะ หรือส่งทางไปรษณีย์ตามช่องทางที่กรมการกงสุลเปิดไว้",
    ],
    en: [
      "Samut Prakan hosts several industrial estates near Suvarnabhumi Airport; corporate documents and foreign-employee files are the common cases.",
      "Legalization is generally filed at the Department of Consular Affairs on Chaeng Watthana, or by post through the department's mail channel.",
    ],
  },
  chonburi: {
    th: [
      "ผู้ถือวีซ่าที่พักในชลบุรีติดต่อสำนักงานตรวจคนเข้าเมืองจังหวัดชลบุรีเรื่องขยายเวลาพำนักและรายงานตัว",
      "ชลบุรีอยู่ในพื้นที่ EEC มีโรงงานและบริษัทต่างชาติจำนวนมาก เคสที่พบบ่อยคือเอกสารบริษัทและใบอนุญาตทำงาน",
      CONFIRM_TH,
    ],
    en: [
      "Visa holders living in Chonburi deal with the Chon Buri Provincial Immigration Office for extensions and reporting.",
      "Chonburi sits in the Eastern Economic Corridor (EEC) with many foreign-owned plants, so corporate papers and work permits are common.",
      CONFIRM_EN,
    ],
  },
  pattaya: {
    th: [
      "ผู้พำนักในพัทยาอยู่ในเขตความรับผิดชอบของสำนักงานตรวจคนเข้าเมืองจังหวัดชลบุรี (ย่านจอมเทียน) สำหรับขยายเวลาพำนักและรายงานตัว 90 วัน",
      "เคสที่พบบ่อยคือผู้เกษียณอายุและคู่สมรสไทย-ต่างชาติ ซึ่งต้องใช้เอกสารแปลจากต่างประเทศหรือหนังสือรับรองจากสถานทูต",
      "งานรับรองนิติกรณ์ส่วนใหญ่ต้องยื่นที่กรมการกงสุล แจ้งวัฒนะ กรุงเทพฯ เราจึงรวบรวมเอกสารให้ครบในรอบเดียวเพื่อลดการเดินทาง",
    ],
    en: [
      "Pattaya residents fall under the Chon Buri Provincial Immigration Office (Jomtien area) for extensions of stay and 90-day reporting.",
      "Common cases are retirees and Thai–foreign couples who need foreign documents translated or embassy letters prepared.",
      "Legalization is usually filed at the Department of Consular Affairs on Chaeng Watthana, Bangkok, so we assemble the full set in one round to save trips.",
    ],
  },
  rayong: {
    th: [
      "ระยองเป็นแหล่งนิคมอุตสาหกรรมในพื้นที่ EEC เคสหลักคือใบอนุญาตทำงาน วีซ่า Non-B และเอกสารบริษัท",
      "ผู้ถือวีซ่าติดต่อสำนักงานตรวจคนเข้าเมืองจังหวัดระยองในเรื่องขยายเวลาพำนัก",
    ],
    en: [
      "Rayong is an EEC industrial hub; work permits, Non-B visas and company documents are the main cases.",
      "Visa holders deal with the Rayong Provincial Immigration Office for extensions of stay.",
    ],
  },
  chiangmai: {
    th: [
      "สำนักงานหนังสือเดินทางชั่วคราว จังหวัดเชียงใหม่ ของกรมการกงสุล มีข้อมูลบริการรับรองนิติกรณ์เอกสาร จึงอาจไม่ต้องเดินทางเข้ากรุงเทพฯ สำหรับบางเคส",
      "เชียงใหม่มีสถานกงสุลใหญ่ของหลายประเทศ เช่น สหรัฐอเมริกาและญี่ปุ่น ซึ่งช่วยเรื่องเอกสารของคนชาตินั้นได้ใกล้ขึ้น",
      CONFIRM_TH,
    ],
    en: [
      "The Department of Consular Affairs' Temporary Passport Office in Chiang Mai publishes legalization information, so some cases may not need a Bangkok trip.",
      "Chiang Mai hosts consulates-general of several countries, including the United States and Japan, which brings nationals' document services closer.",
      CONFIRM_EN,
    ],
  },
  chiangrai: {
    th: [
      "เชียงรายติดชายแดนเมียนมาและลาว เคสที่พบบ่อยคือเอกสารแรงงานข้ามชาติและเอกสารครอบครัวข้ามพรมแดน",
      "งานรับรองนิติกรณ์มักวางแผนยื่นที่เชียงใหม่หรือกรุงเทพฯ ตามที่สำนักงานรับงานประเภทนั้น",
    ],
    en: [
      "Chiang Rai borders Myanmar and Laos; migrant-worker documents and cross-border family papers are common.",
      "Legalization is usually planned via Chiang Mai or Bangkok, depending on which office handles the request.",
    ],
  },
  bangkok: {
    th: [
      "การรับรองเอกสาร (Legalization) ของกระทรวงการต่างประเทศยื่นที่กรมการกงสุล ถนนแจ้งวัฒนะ เขตหลักสี่ ผู้ยื่นควรตรวจช่องทางนัดหมายและเงื่อนไขล่าสุดบนเว็บไซต์กรมการกงสุล",
      "สถานเอกอัครราชทูตต่างประเทศส่วนใหญ่อยู่ในกรุงเทพฯ เอกสารที่ต้องรับรองโดยสถานทูตจึงมักดำเนินการต่อจากกรมการกงสุลได้ในเมืองเดียวกัน แต่แต่ละสถานทูตมีระบบนัดหมายของตนเอง",
      "ผู้ถือวีซ่าที่พำนักในกรุงเทพฯ ติดต่อสำนักงานตรวจคนเข้าเมืองที่ศูนย์ราชการเฉลิมพระเกียรติฯ แจ้งวัฒนะ อาคาร B สำหรับงานขยายเวลาพำนักและรายงานตัว",
      CONFIRM_TH,
    ],
    en: [
      "Ministry of Foreign Affairs legalization is filed at the Department of Consular Affairs on Chaeng Watthana Road, Lak Si; check the current booking channel on the department's website.",
      "Most foreign embassies are in Bangkok, so embassy legalization can usually follow MFA legalization in the same city, but each embassy runs its own appointment system.",
      "Visa holders living in Bangkok handle extensions and reporting at the Immigration office in the Government Complex, Chaeng Watthana, Building B.",
      CONFIRM_EN,
    ],
  },
  khonkaen: {
    th: [
      "ขอนแก่นเป็นศูนย์กลางภาคอีสาน มีสถานกงสุลใหญ่ของ สปป.ลาว และเวียดนาม และมีมหาวิทยาลัยขนาดใหญ่ เคสที่พบบ่อยคือวุฒิการศึกษาและเอกสารสมรส",
      "ผู้ขอหนังสือรับรองความประพฤติ (ตรวจประวัติอาชญากรรม) ยื่นคำขอตามช่องทางของกองทะเบียนประวัติอาชญากร สำนักงานตำรวจแห่งชาติ ควรตรวจขั้นตอนพิมพ์ลายนิ้วมือล่าสุดก่อนเริ่ม",
      CONFIRM_TH,
    ],
    en: [
      "Khon Kaen is the Isan hub, with consulates-general of Laos and Vietnam and a major university; degree certificates and marriage papers are common.",
      "Police clearance applications follow the Royal Thai Police Criminal Records Division channels; check the current fingerprinting step before starting.",
      CONFIRM_EN,
    ],
  },
  udonthani: {
    th: [
      "อุดรธานีมีคู่สมรสไทย-ต่างชาติจำนวนมาก เคสหลักคือจดทะเบียนสมรส แปลทะเบียนสมรส และเอกสารวีซ่า Non-O",
      "อยู่ใกล้ชายแดนหนองคาย-เวียงจันทน์ เอกสารที่ออกใน สปป.ลาว ต้องตรวจขั้นรับรองจากฝั่งลาวก่อนนำมาใช้ในไทย",
    ],
    en: [
      "Udon Thani has many Thai–foreign couples; marriage registration, translated marriage certificates and Non-O visa files are the main cases.",
      "It is close to the Nong Khai–Vientiane border; documents issued in Laos need the Lao-side authentication checked before use in Thailand.",
    ],
  },
  nakhonratchasima: {
    th: [
      "นครราชสีมาเป็นประตูสู่ภาคอีสาน มีนิคมอุตสาหกรรมและสถาบันการศึกษาหลายแห่ง เคสที่พบบ่อยคือวุฒิการศึกษาและเอกสารบริษัท",
      "งานรับรองนิติกรณ์มักยื่นที่กรุงเทพฯ หรือสำนักงานในภูมิภาคที่เปิดรับงานนั้น",
      CONFIRM_TH,
    ],
    en: [
      "Nakhon Ratchasima is the gateway to Isan with industrial estates and colleges; degree certificates and company papers are common.",
      "Legalization is usually filed in Bangkok or at a regional office that handles the request.",
      CONFIRM_EN,
    ],
  },
  phuket: {
    th: [
      "สำนักงานหนังสือเดินทางชั่วคราว ภูเก็ต ของกรมการกงสุล ให้บริการนิติกรณ์เอกสารนอกเหนือจากหนังสือเดินทาง",
      "ภูเก็ตมีผู้แทนกงสุลและกงสุลกิตติมศักดิ์หลายประเทศ และมีชาวต่างชาติพำนักระยะยาวจำนวนมาก เคสหลักคือวีซ่าพำนักระยะยาว สัญญาเช่า และพินัยกรรม",
      CONFIRM_TH,
    ],
    en: [
      "The Department of Consular Affairs' Temporary Passport Office in Phuket provides document legalization in addition to passports.",
      "Phuket has many consular and honorary-consul representatives and a large long-stay foreign community; long-stay visas, leases and wills are typical.",
      CONFIRM_EN,
    ],
  },
  suratthani: {
    th: [
      "ผู้ใช้บริการในสุราษฎร์ธานีจำนวนมากอยู่บนเกาะสมุยและเกาะพะงัน ซึ่งมีสำนักงานตรวจคนเข้าเมืองบนเกาะสมุย",
      "เพื่อลดการเดินทางข้ามเกาะ เราตรวจเอกสารจากไฟล์สแกนก่อน แล้วจึงนัดขั้นที่ต้องใช้ต้นฉบับ",
    ],
    en: [
      "Many Surat Thani clients live on Koh Samui or Koh Phangan; Koh Samui has its own immigration office.",
      "To limit ferry trips we review scans first and only schedule steps that need originals.",
    ],
  },
  songkhla: {
    th: [
      "สงขลามีสถานกงสุลใหญ่มาเลเซียและจีน และอยู่ติดชายแดนมาเลเซีย เคสที่พบบ่อยคือเอกสารครอบครัวข้ามพรมแดนและเอกสารการศึกษา",
      "เอกสารที่ออกในมาเลเซียต้องตรวจขั้นรับรองของฝั่งมาเลเซียก่อนนำมาใช้ในไทย",
      CONFIRM_TH,
    ],
    en: [
      "Songkhla hosts the consulates-general of Malaysia and China and borders Malaysia; cross-border family and education documents are common.",
      "Documents issued in Malaysia need the Malaysian-side authentication checked before use in Thailand.",
      CONFIRM_EN,
    ],
  },
  ubonratchathani: {
    th: [
      "อุบลราชธานีอยู่ใกล้ด่านช่องเม็กชายแดนลาว เคสที่พบบ่อยคือเอกสารแรงงานและเอกสารครอบครัว",
      "เอกสารที่ออกใน สปป.ลาว ต้องตรวจขั้นรับรองของฝั่งลาวก่อนนำมาใช้ในไทย",
    ],
    en: [
      "Ubon Ratchathani is near the Chong Mek border crossing to Laos; labour and family documents are common.",
      "Documents issued in Laos need the Lao-side authentication checked before use in Thailand.",
    ],
  },
};

export function provinceNote(slug: string): ProvinceNote | undefined {
  return PROVINCE_NOTES[slug];
}
